import React from 'react';
import { BulbOutlined, ArrowRightOutlined, RightCircleOutlined } from '@ant-design/icons';
import { Button, Tag } from 'antd';

interface SOPItem {
  condition: string;
  action: string;
}

interface Props {
  items: SOPItem[];
  dmaicPhase?: string;
  onModuleJump?: (moduleName: string) => void;
}

const DMAIC_COLORS: Record<string, string> = {
  Define: '#9c27b0',
  Measure: '#2196f3',
  Analyze: '#ff9800',
  Improve: '#4caf50',
  Control: '#f44336',
};

// Module name patterns to detect in SOP text → make clickable
const MODULE_PATTERNS: Array<{ pattern: RegExp; module: string; label: string }> = [
  { pattern: /模块\s*1|基本统计|基础统计|描述统计/g, module: 'basic_stats', label: '基本统计' },
  { pattern: /模块\s*2|过程能力|Cpk|Ppk|能力分析/g, module: 'process_capability', label: '过程能力' },
  { pattern: /模块\s*3|SPC|控制图/g, module: 'spc', label: 'SPC控制图' },
  { pattern: /模块\s*4|假设检验|t检验|ANOVA/g, module: 'hypothesis', label: '假设检验' },
  { pattern: /模块\s*5|MSA|测量系统|GR&R|量具/g, module: 'msa', label: 'MSA' },
  { pattern: /模块\s*6|回归分析|回归/g, module: 'regression', label: '回归分析' },
  { pattern: /模块\s*7|DOE|实验设计|因子/g, module: 'doe', label: 'DOE' },
  { pattern: /模块\s*8|时间序列|ARIMA|趋势/g, module: 'time_series', label: '时间序列' },
  { pattern: /模块\s*9|多变量|PCA|聚类/g, module: 'multivariate', label: '多变量' },
  { pattern: /模块\s*10|可靠性|Weibull|寿命/g, module: 'reliability', label: '可靠性' },
  { pattern: /模块\s*11|非参数|Wilcoxon|Kruskal/g, module: 'nonparametric', label: '非参数' },
  { pattern: /模块\s*12|卡方|独立性检验/g, module: 'chi_square', label: '卡方' },
];

function extractModuleLinks(text: string): string[] {
  const found: string[] = [];
  for (const { pattern, module } of MODULE_PATTERNS) {
    pattern.lastIndex = 0;
    if (pattern.test(text)) {
      if (!found.includes(module)) found.push(module);
    }
  }
  return found;
}

const SOPAdvice: React.FC<Props> = ({ items, dmaicPhase, onModuleJump }) => {
  if (!items || items.length === 0) return null;

  return (
    <div className="card-trendata" style={{ padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <BulbOutlined style={{ fontSize: 16, color: '#fa8c16' }} />
        <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#061b31' }}>
          SOP 建议
        </h4>
        {dmaicPhase && (
          <Tag style={{
            background: DMAIC_COLORS[dmaicPhase] || '#533afd',
            color: '#fff', fontWeight: 600, border: 'none', fontSize: 11,
          }}>
            DMAIC: {dmaicPhase}
          </Tag>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((item, i) => {
          const linkedModules = extractModuleLinks(item.action);
          return (
            <div key={i} style={{
              padding: '10px 14px',
              background: i === 0 ? '#f6ffed' : '#fafafa',
              borderRadius: 8, border: `1px solid ${i === 0 ? '#b7eb8f' : '#f0f0f0'}`,
            }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <ArrowRightOutlined style={{ color: '#533afd', marginTop: 3, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: '#999', marginBottom: 2 }}>{item.condition}</div>
                  <div style={{ fontSize: 13, color: '#333', lineHeight: 1.6 }}>{item.action}</div>
                </div>
              </div>
              {/* Clickable module jump buttons */}
              {linkedModules.length > 0 && onModuleJump && (
                <div style={{ display: 'flex', gap: 6, marginTop: 8, marginLeft: 24 }}>
                  {linkedModules.map(mod => {
                    const info = MODULE_PATTERNS.find(p => p.module === mod);
                    return (
                      <Button
                        key={mod}
                        size="small"
                        type="primary"
                        ghost
                        icon={<RightCircleOutlined />}
                        onClick={() => onModuleJump(mod)}
                        style={{ fontSize: 11, borderRadius: 4 }}
                      >
                        跳转 {info?.label || mod}
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SOPAdvice;
